import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

export type ValidBreakpoint = (typeof Breakpoints)[keyof typeof Breakpoints];

@Injectable({
  providedIn: 'root',
})
export class BreakpointService {
  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly breakpointObserver: BreakpointObserver
  ) {}

  /**
   * Checks if the current viewport matches a specified breakpoint.
   *
   * @param breakpoints A breakpoint value from Angular CDK's `Breakpoints`
   * @returns An observable that emits `true` if the breakpoint matches, otherwise `false`.
   */
  isMatchingBreakpoint(breakpoints: ValidBreakpoint | ValidBreakpoint[]): Observable<boolean> {
    const validBreakpoints = Object.values(Breakpoints);

    if (Array.isArray(breakpoints)) {
      if (!breakpoints.every((bp) => validBreakpoints.includes(bp)))
        throw new Error(`Invalid breakpoint(s) passed to isMatchingBreakpoint: ${breakpoints}`);
    } else if (!validBreakpoints.includes(breakpoints))
      throw new Error(`Invalid breakpoint passed to isMatchingBreakpoint: ${breakpoints}`);

    if (!isPlatformBrowser(this.platformId)) return of(false);

    return this.breakpointObserver.observe(breakpoints).pipe(
      map((state) => state.matches),
      shareReplay(1)
    );
  }

  isMobile$(): Observable<boolean> {
    return this.isMatchingBreakpoint(Breakpoints.Handset);
  }

  isNotMobile$(): Observable<boolean> {
    return this.isMatchingBreakpoint([
      Breakpoints.Tablet,
      Breakpoints.Web,
      Breakpoints.WebLandscape,
      Breakpoints.WebPortrait,
    ]);
  }
}
